/**
 * Utilidades para gestión de tokens según el nuevo sistema de refresh reactivo
 */

/**
 * Verifica si el access token ha expirado localmente
 * @returns {boolean} true si el token está expirado o próximo a expirar
 */
export const isTokenExpiredLocally = () => {
  const tokenExpiresAt = localStorage.getItem("tokenExpiresAt");
  
  if (!tokenExpiresAt) {
    return false; // No sabemos, asumir válido
  }

  const expirationDate = new Date(tokenExpiresAt);
  const now = new Date();
  
  // Añadir buffer de 30 segundos para evitar race conditions
  return expirationDate <= new Date(now.getTime() + 30000);
};

/**
 * Verifica si el refresh token ha expirado localmente
 * @returns {boolean} true si el refresh token está expirado
 */
export const isRefreshTokenExpiredLocally = () => {
  const refreshTokenExpiresAt = localStorage.getItem("refreshTokenExpiresAt");
  
  if (!refreshTokenExpiresAt) {
    return false; // No sabemos, asumir válido
  }

  const expirationDate = new Date(refreshTokenExpiresAt);
  const now = new Date();
  
  return expirationDate <= now;
};

/**
 * Verifica el estado de expiración del refresh token y retorna información
 * para mostrar avisos al usuario
 * @returns {Object|null} Estado de expiración o null si todo está bien
 */
export const checkRefreshTokenExpiration = () => {
  const refreshTokenExpiresAt = localStorage.getItem("refreshTokenExpiresAt");
  
  if (!refreshTokenExpiresAt) {
    return null;
  }

  const expirationDate = new Date(refreshTokenExpiresAt);
  const now = new Date();
  const daysUntilExpiration = Math.floor((expirationDate - now) / (1000 * 60 * 60 * 24));

  // Si expira en menos de 7 días, mostrar aviso
  if (daysUntilExpiration <= 7 && daysUntilExpiration > 0) {
    return {
      warning: true,
      daysRemaining: daysUntilExpiration,
      message: `Tu sesión expirará en ${daysUntilExpiration} día(s). Por favor, vuelve a iniciar sesión para extender tu sesión.`
    };
  }

  // Si ya expiró
  if (daysUntilExpiration <= 0) {
    return {
      expired: true,
      message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente."
    };
  }

  return null;
};

/**
 * Guarda las fechas de expiración en localStorage junto con los tokens actualizados
 * @param {Object} data - Datos del servidor con token, refreshToken, tokenExpiresAt, refreshTokenExpiresAt
 */
export const persistTokenData = (data) => {
  if (data.tokenExpiresAt) {
    const tokenExpiresAtStr = data.tokenExpiresAt instanceof Date 
      ? data.tokenExpiresAt.toISOString() 
      : data.tokenExpiresAt;
    localStorage.setItem("tokenExpiresAt", tokenExpiresAtStr);
  }
  
  if (data.refreshTokenExpiresAt) {
    const refreshTokenExpiresAtStr = data.refreshTokenExpiresAt instanceof Date 
      ? data.refreshTokenExpiresAt.toISOString() 
      : data.refreshTokenExpiresAt;
    localStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAtStr);
  }
};

/**
 * Limpia todas las fechas de expiración del localStorage
 */
export const clearTokenData = () => {
  localStorage.removeItem("tokenExpiresAt");
  localStorage.removeItem("refreshTokenExpiresAt");
};

/**
 * Limpia completamente todos los datos de sesión del localStorage
 */
export const clearSessionData = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("tokenExpiresAt");
  localStorage.removeItem("refreshTokenExpiresAt");
};
